import React from "react";

import { useMutation } from "@apollo/client";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Dialog,
  FormHelperText,
  IconButton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { Controller } from "react-hook-form";
import { makeStyles, withStyles } from "tss-react/mui";
import * as yup from "yup";

import { graphql } from "@/gql";
import useFormWithYup from "@/hooks/useFormWithYup";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import { colors } from "@/theme/overrides/colors";
import MealRatingIcon from "@pages/Clients/ClientDetails/ClientDiary/Meals/components/MealRatingIcon";

type Props = {
  visibility: boolean;
  closeDialog(): void;
};

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: theme.spacing(5, 12, 10, 12),
    overflowY: "auto",
  },
  header: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    margin: theme.spacing(5, 3, 1, 5),
  },
  headerText: {
    fontSize: "24px",
    fontWeight: "bold",
  },
  upperLabel: {
    fontSize: "36px",
  },
  lowerLabel: {
    fontSize: "18px",
  },
  body: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "8px",
    marginBottom: "46px",
    border: "5px solid #EDF0F5",
    padding: "72px",
    width: "400px",
  },
  optionField: {
    marginBottom: "14px",
  },
  image: {
    with: "300px",
    height: "300px",
    objectFit: "contain",
  },
  ratingButton: {
    border: "1px solid transparent",
    "&.MuiToggleButton-root": {
      borderRadius: 9999,
      opacity: 0.5,
    },
    "&.Mui-selected": {
      backgroundColor: "inherit",
      opacity: 1.0,
    },
    "&.Mui-selected:hover": {
      backgroundColor: "inherit",
    },
  },
}));

const StyledTextField = withStyles(TextField, {
  root: {
    width: "100%",
    marginTop: "24px",
  },
});

const StyledSaveButton = withStyles(Button, {
  root: {
    backgroundColor: colors.primary,
    marginTop: "24px",
    "&:hover": {
      backgroundColor: colors.primary,
    },
  },
});

export type FormValues = {
  rating: number | null;
  reason: string;
};

const validationSchema = yup
  .object({
    rating: yup
      .number()
      .required("Please choose a rating")
      .typeError("Please choose a rating"),
    reason: yup.string().presence(),
  })
  .defined();

const createMealPlanFeedbackMutation = graphql(`
  mutation CreateMealPlanFeedback($input: CreateMealPlanFeedbackInput!) {
    createMealPlanFeedback(input: $input) {
      mealPlanFeedback {
        id
        coachId
        reason
        rating
      }
      errors {
        fullMessage
      }
    }
  }
`);

const MealPlannerSurvey: React.FC<Props> = ({ visibility, closeDialog }) => {
  const { classes } = useStyles();
  const [createMealPlanfeedback] = useMutation(createMealPlanFeedbackMutation);
  const feedbackOptions = [1, 2, 3, 4, 5];
  const snackbar = useGlobalSnackbar();

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useFormWithYup({
    defaultValues: {
      rating: "",
      reason: "",
    },
    validationSchema,
  });

  async function onSubmit(values: yup.InferType<typeof validationSchema>) {
    const input = {
      rating: values.rating,
      reason: values.reason,
    };
    try {
      const result = await createMealPlanfeedback({
        variables: { input: input },
      });
      closeDialog();
      if (result.data?.createMealPlanFeedback?.errors) {
        snackbar.open(
          result?.data?.createMealPlanFeedback?.errors[0]?.fullMessage
        );
      } else {
        snackbar.open("Thank you for your feedback!");
      }
    } catch (e: any) {
      snackbar.open(e.message);
    }
  }

  function closeMealPlanSurveyDialog() {
    closeDialog();
  }
  return (
    <Dialog open={visibility} onClose={closeMealPlanSurveyDialog} maxWidth="sm">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box className={classes.header}>
          <Typography className={classes.headerText}>
            How do you feel about Meal Planner?
          </Typography>
          <IconButton onClick={closeMealPlanSurveyDialog} size="large">
            <CloseIcon />
          </IconButton>
        </Box>
        <Box className={classes.root}>
          <Typography variant="subtitle2">Choose your rating</Typography>

          <Box flexDirection="row">
            <Controller
              control={control}
              name="rating"
              render={({ field: { value, onChange } }) => (
                <ToggleButtonGroup
                  value={value}
                  exclusive
                  onChange={(_, newValue) => onChange(newValue)}
                >
                  {feedbackOptions.map((value) => (
                    <ToggleButton
                      className={classes.ratingButton}
                      key={value}
                      aria-label={`rating ${value}`}
                      size="small"
                      value={value}
                    >
                      <MealRatingIcon rating={value} />
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              )}
            />
          </Box>
          {!!errors.rating && (
            <FormHelperText error>{errors.rating.message}</FormHelperText>
          )}

          <StyledTextField
            {...register("reason")}
            placeholder="Why do you feel that way?"
            multiline
          />
          <StyledSaveButton type="submit">
            {isSubmitting ? "Sending..." : "Send"}
          </StyledSaveButton>
        </Box>
      </form>
    </Dialog>
  );
};

export default MealPlannerSurvey;
